import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { ThemeProvider as MUIThemeProvider, PaletteMode } from '@mui/material/styles';
import { createAppTheme, ThemePreset } from '../styles/theme';
import { useLayout } from './LayoutContext';

interface ThemeContextType {
  mode: PaletteMode;
  toggleMode: () => void;
  setMode: (mode: PaletteMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface AppThemeProviderProps {
  children: React.ReactNode;
}

export const AppThemeProvider: React.FC<AppThemeProviderProps> = ({ children }) => {
  const { preferences } = useLayout();
  
  // Get initial theme from localStorage or default to light for classic
  const getInitialMode = (): PaletteMode => {
    const savedMode = localStorage.getItem('themeMode');
    if (savedMode === 'light' || savedMode === 'dark') {
      return savedMode as PaletteMode;
    }
    
    // For classic theme preset, always default to light
    if (preferences.themePreset === 'classic') {
      return 'light';
    }
    
    // For modern theme, check system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  };

  const [mode, setMode] = useState<PaletteMode>(getInitialMode);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      // Only update if user hasn't explicitly set a preference
      if (!localStorage.getItem('themeMode')) {
        setMode(e.matches ? 'dark' : 'light');
      }
    };

    // Check if addEventListener is supported
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  const toggleMode = () => {
    const newMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
    localStorage.setItem('themeMode', newMode);
    updateThemeColor(newMode, preferences.themePreset);
  };

  const setModeWithStorage = (newMode: PaletteMode) => {
    setMode(newMode);
    localStorage.setItem('themeMode', newMode);
    updateThemeColor(newMode, preferences.themePreset);
  };

  const updateThemeColor = (currentMode: PaletteMode, preset: ThemePreset) => {
    // Update the theme-color meta tag for PWA titlebar
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      let color: string;
      if (preset === 'classic') {
        // Classic InvaroSoft titlebar color (same in light/dark)
        color = '#2b3e7d';  // ThemeInvaroSoftTopBarColor from old app
      } else {
        // Modern theme colors
        color = currentMode === 'dark' ? '#1a5c2e' : '#289546';
      }
      metaThemeColor.setAttribute('content', color);
    }
  };

  // Update theme color when mode or preset changes
  useEffect(() => {
    updateThemeColor(mode, preferences.themePreset);
  }, [mode, preferences.themePreset]);

  const theme = useMemo(() => createAppTheme(mode, preferences.themePreset), [mode, preferences.themePreset]);

  const contextValue = useMemo(
    () => ({
      mode,
      toggleMode,
      setMode: setModeWithStorage,
    }),
    [mode]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <MUIThemeProvider theme={theme}>
        {children}
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
};